import { cn } from '@ifixit/ui/lib/utils';
import {
   DropdownMenu,
   DropdownMenuContent,
   DropdownMenuItem,
   DropdownMenuTrigger,
} from '@ifixit/ui/components/dropdown-menu';
import { FaIcon } from '@ifixit/ui/components/icon';
import { SentryError } from '@ifixit/sentry';
import isEqual from 'lodash/isEqual';
import {
   type ComponentProps,
   type ReactElement,
   type RefObject,
   memo,
   useCallback,
   useState,
} from 'react';
import { hiddenWrapContainerClasses, useHiddenWrap } from './FlexHiddenWrap';

export interface BreadcrumbData {
   name: string;
   title?: string;
   url?: string;
   onClick?: () => void;
}

export type BreadCrumbsProps = {
   breadCrumbs: BreadcrumbData[];
   breadcrumbsToShow?: number;
   breadcrumbIcon?: ReactElement;
   includeSchema?: boolean;
} & ComponentProps<'div'>;

export type StaticBreadCrumbsProps = {
   visibleBreadCrumbs: BreadcrumbData[];
   collapsedBreadCrumbs?: BreadcrumbData[];
   breadcrumbIcon?: ReactElement;
   includeSchema?: boolean;
   listRef?: RefObject<HTMLOListElement | null>;
} & ComponentProps<'div'>;

type IFixitBreadcrumbItemProps = BreadcrumbData & {
   /** 1-based position from the top of the hierarchy, for the schema.org meta. */
   position: number;
   isFirst: boolean;
   isLast: boolean;
   includeSchema: boolean;
   breadcrumbIcon?: ReactElement;
};

export const BreadCrumbs = memo(function DynamicBreadCrumbs({
   breadCrumbs,
   breadcrumbsToShow,
   breadcrumbIcon = <DefaultBreadcrumbIcon />,
   includeSchema = true,
   ...divProps
}: BreadCrumbsProps) {
   if (breadcrumbsToShow != undefined && breadcrumbsToShow <= 0) {
      throw new SentryError('breadcrumbsToShow can only be undefined or must be greater than 0');
   }

   // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
   const isStatic = breadcrumbsToShow > 0;

   const visibleBreadCrumbs = isStatic
      ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        breadCrumbs.slice(breadCrumbs.length - breadcrumbsToShow)
      : breadCrumbs;

   const hiddenBreadCrumbs = isStatic
      ? // @ts-expect-error TS(2532) FIXME: Object is possibly 'undefined'.
        breadCrumbs.slice(0, breadCrumbs.length - breadcrumbsToShow)
      : [];

   const [wrappedBreadcrumbs, setWrappedBreadcrumbs] =
      useState<BreadcrumbData[]>(hiddenBreadCrumbs);

   const wrappedChildrenEffect = useCallback(
      (children: HTMLElement[]) => {
         if (isStatic) {
            return;
         }
         const wrappedNames = new Set(children.map(c => c.dataset.name));
         const wrapped = breadCrumbs.filter(b => wrappedNames.has(b.name));
         setWrappedBreadcrumbs(() => wrapped);
      },
      [breadCrumbs, isStatic]
   );

   const { ref: listRef } = useHiddenWrap<HTMLOListElement>({ wrappedChildrenEffect });

   return (
      <StaticBreadCrumbs
         visibleBreadCrumbs={visibleBreadCrumbs}
         collapsedBreadCrumbs={wrappedBreadcrumbs}
         breadcrumbIcon={breadcrumbIcon}
         includeSchema={includeSchema}
         listRef={listRef}
         {...divProps}
      />
   );
});

export function StaticBreadCrumbs({
   visibleBreadCrumbs,
   collapsedBreadCrumbs = [],
   breadcrumbIcon = <DefaultBreadcrumbIcon />,
   includeSchema = true,
   listRef,
   className,
   ...rest
}: StaticBreadCrumbsProps) {
   return (
      <div className={cn('flex shrink grow items-center gap-1.5', className)} {...rest}>
         <IFixitCollapsedBreadcrumb
            breadCrumbs={collapsedBreadCrumbs}
            breadcrumbIcon={breadcrumbIcon}
         />
         <IFixitBreadcrumb
            includeSchema={includeSchema}
            breadCrumbs={visibleBreadCrumbs}
            breadcrumbIcon={breadcrumbIcon}
            listRef={listRef}
         />
      </div>
   );
}

function DefaultBreadcrumbIcon() {
   return (
      <div className="flex">
         <FaIcon
            name="chevron-right"
            className="mt-px h-2.5 w-auto aspect-[320/512] text-gray-400"
         />
      </div>
   );
}

const IFixitBreadcrumb = memo(function IFixitBreadcrumb({
   breadCrumbs,
   breadcrumbIcon,
   listRef,
   includeSchema,
}: Pick<BreadCrumbsProps, 'breadCrumbs' | 'breadcrumbIcon' | 'includeSchema'> & {
   listRef?: RefObject<HTMLOListElement | null>;
}) {
   const reversedBreadCrumbs = breadCrumbs.slice().reverse();

   const BreadCrumbItems = reversedBreadCrumbs.map((crumb, index) => {
      const isFirstBreadCrumb = index === reversedBreadCrumbs.length - 1;
      const isLastBreadCrumb = index === 0;
      return (
         <IFixitBreadcrumbItem
            key={crumb.name}
            {...crumb}
            // `index` walks the reversed list, deepest first, because the list
            // paints with `flex-row-reverse`. schema.org counts the other way,
            // from the top of the hierarchy, which is also how the server
            // placeholder in NavBreadcrumbsComponent numbers them.
            position={reversedBreadCrumbs.length - index}
            isFirst={isFirstBreadCrumb}
            isLast={isLastBreadCrumb}
            // @ts-expect-error TS(2322) FIXME: Type 'boolean | undefined' is not assignable to ty... Remove this comment to see the full error message
            includeSchema={includeSchema}
            breadcrumbIcon={breadcrumbIcon}
         />
      );
   });

   const breadcrumbSchema = includeSchema
      ? {
           itemScope: true,
           itemProp: 'breadcrumb',
           itemType: 'http://schema.org/BreadcrumbList',
        }
      : {};

   return (
      <nav aria-label="breadcrumb" {...breadcrumbSchema}>
         <ol
            ref={listRef}
            className={cn(
               hiddenWrapContainerClasses,
               'h-5 flex-row-reverse items-center justify-end p-0 my-[1em] list-none leading-5'
            )}
         >
            {BreadCrumbItems}
         </ol>
      </nav>
   );
}, isEqual);

const IFixitBreadcrumbItem = memo(function IFixitBreadcrumbItem({
   url,
   name,
   onClick,
   isFirst,
   isLast,
   position,
   includeSchema,
   breadcrumbIcon,
}: IFixitBreadcrumbItemProps) {
   const breadcrumbItemSchema = includeSchema
      ? {
           itemScope: true,
           itemProp: 'itemListElement',
           itemType: 'http://schema.org/ListItem',
        }
      : {};

   const MetaTags = includeSchema ? (
      <>
         <meta itemProp="item" content={url} />
         <meta itemProp="name" content={name} />
         <meta itemProp="position" content={`${position}`} />
      </>
   ) : null;

   return (
      <li data-name={name} {...breadcrumbItemSchema} className="inline-flex items-center">
         <div className="flex transition-transform duration-100 active:scale-105">
            <a
               href={url}
               onClick={onClick}
               className={cn(
                  'line-clamp-1 cursor-pointer hover:no-underline',
                  isLast
                     ? 'font-medium text-gray-900 visited:text-gray-900'
                     : 'font-normal text-gray-600 visited:text-gray-600',
                  isFirst && 'pl-0.5',
                  isLast && 'pr-0.5'
               )}
            >
               {name}
            </a>
         </div>
         {MetaTags}
         {!isLast && (
            <span role="presentation" className="mx-2">
               {breadcrumbIcon}
            </span>
         )}
      </li>
   );
}, isEqual);

const IFixitCollapsedBreadcrumb = memo(function IFixitCollapsedBreadcrumb({
   breadCrumbs,
   breadcrumbIcon,
}: Pick<BreadCrumbsProps, 'breadCrumbs' | 'breadcrumbIcon'>) {
   if (!breadCrumbs.length) {
      return null;
   }

   const CollapsedBreadCrumbItems = breadCrumbs.map(({ name, url, onClick }) => (
      <IFixitCollapsedBreadcrumbItem url={url} name={name} onClick={onClick} key={name} />
   ));

   return (
      <>
         <DropdownMenu>
            <DropdownMenuTrigger asChild>
               <button
                  type="button"
                  aria-label="Options"
                  // 576px is Chakra's `sm`, the breakpoint the old `minWidth`
                  // prop used. Tailwind's `sm` is 640px here (only
                  // `--breakpoint-xxs` is overridden in base.css), so the icon
                  // below still grows one breakpoint later than the button
                  // does. That split predates this migration and is kept so the
                  // rendering matches Chakra's exactly.
                  className="mr-1 inline-flex h-6 shrink-0 min-w-[26px] items-center justify-center rounded bg-gray-300 hover:bg-gray-200 min-[576px]:min-w-8"
               >
                  <FaIcon
                     name="ellipsis"
                     style="regular"
                     className="h-3.5 w-auto aspect-[448/512] text-gray-500 sm:h-4"
                  />
               </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
               {CollapsedBreadCrumbItems.reverse()}
            </DropdownMenuContent>
         </DropdownMenu>
         {breadcrumbIcon}
      </>
   );
}, isEqual);

function IFixitCollapsedBreadcrumbItem({ url, name, onClick }: BreadcrumbData) {
   return (
      <DropdownMenuItem asChild>
         <a
            href={url}
            onClick={onClick}
            className="text-sm text-gray-900 visited:text-gray-900 hover:no-underline"
         >
            {name}
         </a>
      </DropdownMenuItem>
   );
}
